import React, { FC, useCallback } from 'react';

import { EAssetType, EEmptyStateDesignType } from 'constants/enums';
import { useFormikContext } from 'formik';

import { Label } from 'components/ui/label';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import UploadImage from 'components/UploadImage/UploadImage';

import type { EmptyStateFormValues } from '../storeSettingsView.types';

import { FormField } from './FormField';

const backgroundImageHelperText =
  'Upload a background image for the empty state card. 1:1 Ratio';

interface EmptyStateSectionFieldsProps {
  reconnectOn?: boolean;
}

export const EmptyStateSectionFields: FC<EmptyStateSectionFieldsProps> = ({
  reconnectOn = false
}) => {
  const {
    values,
    handleBlur,
    handleChange,
    setFieldValue,
    errors,
    touched,
    submitCount
  } = useFormikContext<EmptyStateFormValues>();

  const isCustomDesign = values.designType === EEmptyStateDesignType.CUSTOM;
  const showCtaButtonUpload = isCustomDesign && reconnectOn;
  const backgroundImageError =
    submitCount > 0 && Boolean(errors.backgroundImageUrl);
  const ctaButtonImageError =
    submitCount > 0 && Boolean(errors.ctaButtonImageUrl);

  const handleDesignTypeChange = useCallback(
    (designType: string) => {
      setFieldValue('designType', designType);
    },
    [setFieldValue]
  );

  const handleBackgroundImageUpload = useCallback(
    (backgroundImageUrl: string) => {
      setFieldValue('backgroundImageUrl', backgroundImageUrl);
    },
    [setFieldValue]
  );

  const handleCtaButtonImageUpload = useCallback(
    (ctaButtonImageUrl: string) => {
      setFieldValue('ctaButtonImageUrl', ctaButtonImageUrl);
    },
    [setFieldValue]
  );

  return (
    <div className="mt-1 grid grid-cols-1 gap-9 md:grid-cols-2 md:col-span-2 [&_input:not([aria-invalid])]:border-zinc-200 [&_input]:placeholder:text-base-mutedForeground">
      <RadioGroup
        value={values.designType}
        onValueChange={handleDesignTypeChange}
        className="flex flex-row gap-9 md:col-span-2"
      >
        <div className="flex items-start gap-2">
          <RadioGroupItem
            value={EEmptyStateDesignType.TEXT}
            id="empty-state-text"
            className="mt-0.5 shrink-0 border-base-border text-indigo-600 shadow-none focus-visible:ring-indigo-600"
            data-testid="emptyStateDesignTypeText"
          />
          <Label
            htmlFor="empty-state-text"
            className="cursor-pointer font-sans text-sm font-medium text-base-foreground"
          >
            Text Only
          </Label>
        </div>
        <div className="flex items-start gap-2">
          <RadioGroupItem
            value={EEmptyStateDesignType.CUSTOM}
            id="empty-state-custom"
            className="mt-0.5 shrink-0 border-base-border text-indigo-600 shadow-none focus-visible:ring-indigo-600"
            data-testid="emptyStateDesignTypeCustom"
          />
          <Label
            htmlFor="empty-state-custom"
            className="cursor-pointer font-sans text-sm font-medium text-base-foreground"
          >
            Custom Design
          </Label>
        </div>
      </RadioGroup>

      {!isCustomDesign && (
        <>
          <FormField
            label="Empty State Title"
            name="noOffersTitleText"
            value={values.noOffersTitleText}
            onChange={handleChange}
            onBlur={handleBlur}
            error={
              touched.noOffersTitleText && Boolean(errors.noOffersTitleText)
            }
            helperText={
              touched.noOffersTitleText
                ? errors.noOffersTitleText?.toString()
                : undefined
            }
            placeholder="No Offers Available"
            dataTestId="noOffersTitleText"
          />
          <FormField
            label="Empty State Body Text"
            name="noOffersMessageText"
            value={values.noOffersMessageText}
            onChange={handleChange}
            onBlur={handleBlur}
            error={
              touched.noOffersMessageText &&
              Boolean(errors.noOffersMessageText)
            }
            helperText={
              touched.noOffersMessageText
                ? errors.noOffersMessageText?.toString()
                : undefined
            }
            placeholder="No offers are available at the moment. Try again soon!"
            dataTestId="noOffersMessageText"
          />
        </>
      )}

      {isCustomDesign && (
        <>
          <div className="space-y-2">
            <UploadImage
              title="Background Image"
              description={backgroundImageHelperText}
              buttonText="Upload"
              onUpload={handleBackgroundImageUpload}
              selectedImageUrl={values.backgroundImageUrl}
              forceType={EAssetType.GENERAL}
              variant="storeSettings"
              className="w-full"
              isRequired
              hasError={backgroundImageError}
              errorMessage={errors.backgroundImageUrl?.toString()}
              data-testid="emptyStateBackgroundImage"
              buttonDataTestId="uploadEmptyStateBackgroundImage"
            />
          </div>
          {showCtaButtonUpload && (
            <div className="space-y-2">
              <UploadImage
                title="CTA Button Image"
                buttonText="Upload"
                onUpload={handleCtaButtonImageUpload}
                selectedImageUrl={values.ctaButtonImageUrl}
                forceType={EAssetType.GENERAL}
                variant="storeSettings"
                className="w-full"
                isRequired
                hasError={ctaButtonImageError}
                errorMessage={errors.ctaButtonImageUrl?.toString()}
                data-testid="emptyStateCtaButtonImage"
                buttonDataTestId="uploadEmptyStateCtaButtonImage"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};
